
import {defineComponent, ref, watch } from 'vue';
import { useTrucodeReferenceStore } from 'stores/references-store';

export default defineComponent({
  props: {
    articleId: {
      type: String,
      default: ''
    },
    highlightMatchedTerms: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const referencesStore = useTrucodeReferenceStore();

    const currentTermElement = ref<number>(-1);
    const navigateToFirstTerm = ref<boolean>(false);
    const navigateToLastTerm = ref<boolean>(false);
    const content = ref<string | undefined>(undefined);
    const contentContainer = ref<HTMLElement | null>(null);
    const highlightedElements = ref<NodeListOf<Element>>();
    const highlightedContent = ref<string>();

    const highlightAndNavigate = (step: number) => {
      setTimeout(() => {
        const maxNodeLength = highlightedElements.value?.length??0;
        highlightedElements.value?.forEach(item => item.classList.remove('io-mfe-ig-highlight-active'))
        if (maxNodeLength > 0 && maxNodeLength - 1 >= step) {
          highlightedElements.value?.item(step)?.classList.add('io-mfe-ig-highlight-active');
          highlightedElements.value?.item(step)?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      }, 0);
    }

    const buildHighlightedContent = () => {
      const searchTerm = referencesStore.getCurrentTerm();
      if (props.highlightMatchedTerms && searchTerm) {
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        highlightedContent.value = content.value?.replace(regex, '<span class="io-mfe-ig-highlight">$1</span>');
        setTimeout(() => {
          highlightedElements.value = contentContainer.value?.querySelectorAll('.io-mfe-ig-highlight');
          if(navigateToFirstTerm.value) {
            currentTermElement.value = 0
            highlightAndNavigate(0);
            navigateToFirstTerm.value = false
          } else if(navigateToLastTerm.value) {
            const lastStep = highlightedElements.value?.length??0;
            currentTermElement.value = lastStep - 1
            highlightAndNavigate(currentTermElement.value);
            navigateToLastTerm.value = false
          }  else {
            currentTermElement.value = -1;
          }
        }, 0);
      } else {
        highlightedContent.value = content.value;
      }
    }

    watch(
        () => props.articleId,
        async (newArticleId) => {
          if (newArticleId) {
            content.value = undefined;
            highlightedContent.value = undefined;
            currentTermElement.value = -1;
            await useTrucodeReferenceStore().getReferencesArticle(newArticleId)?.then((newContent) => {
              content.value = newContent;
              buildHighlightedContent();
            });
          }
        },
        { immediate: true }
    );

    watch(
        () => props.highlightMatchedTerms,
        async () => {
          currentTermElement.value = -1;
          buildHighlightedContent();
        },
        { immediate: true }
    );

    return {
      content,
      contentContainer,
      highlightedContent,
      currentTermElement,
      navigateToFirstTerm,
      navigateToLastTerm,
      referencesStore,
      highlightedElements
    };
  },
  methods: {
    handleNextTermClick() {
      const maxNodeLength = this.highlightedElements?.length??0;

      if(this.currentTermElement < maxNodeLength - 1) {
        this.currentTermElement = this.currentTermElement + 1;
        this.highlightAndNavigate(this.currentTermElement);
      } else {
        this.navigateToFirstTerm = true;
        this.referencesStore.nextArticle();
      }
    },
    handlePreviousTermClick() {
      if(this.currentTermElement > 0) {
        this.currentTermElement = this.currentTermElement - 1
        this.highlightAndNavigate(this.currentTermElement);
      } else {
        this.navigateToLastTerm = true;
        this.referencesStore.previousArticle()
      }
    },
    highlightAndNavigate(step: number) {
      setTimeout(() => {
        this?.highlightedElements?.forEach(item => item.classList.remove('io-mfe-ig-highlight-active'))
        const maxNodeLength = this.highlightedElements?.length??0;
        if (maxNodeLength > 0 && maxNodeLength - 1 >= step) {
          this?.highlightedElements?.item(step)?.classList.add('io-mfe-ig-highlight-active');
          this?.highlightedElements?.item(step)?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      }, 0);
    }
  },
})
